/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { ListViewProps } from '@airasia-phoenix/react-native/components/ListView';
import type { TextFieldRefs } from '@airasia-phoenix/react-native/components/TextFieldView/types';
import { getColor, pd } from '@airasia-phoenix/react-native/utils';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Keyboard, type TextStyle } from 'react-native';
import {
  getEmailDomainDropdownHeight,
  getEmailDomainSuggestions,
  getTypedEmailPrefixForHighlight,
  isCompleteEmailPaste,
} from '../utils/emailDomainSuggestions';
import { isValidEmail } from '../utils/validations';

type UseEmailDomainFieldParams = {
  initialEmail?: string;
  invalidEmailMessage: string;
  /** Called only when the field actually gains focus, not on repeat reports. */
  onEmailFocus?: () => void;
};

const buildEmailDomainHighlightText = (
  email: string,
  suggestion: string,
  typedStyle: TextStyle,
  domainStyle: TextStyle,
) => {
  const typedPrefix = getTypedEmailPrefixForHighlight(email, suggestion);
  if (!typedPrefix) {
    return undefined;
  }

  const domainPart = suggestion.slice(typedPrefix.length);
  const spans = [
    {
      spannable: typedPrefix,
      style: typedStyle,
    },
  ];

  if (domainPart) {
    spans.push({
      spannable: domainPart,
      style: domainStyle,
    });
  }

  return spans;
};

export const useEmailDomainField = ({
  initialEmail = '',
  invalidEmailMessage,
  onEmailFocus,
}: UseEmailDomainFieldParams) => {
  const emailFieldRef = useRef<TextFieldRefs>(null);
  // Last focus state reported by TextFieldView. It re-reports focus whenever
  // `isError` flips, so a submit error would otherwise clear itself.
  const lastReportedFocusRef = useRef(false);
  // Skips stale blur validation after a dropdown selection updates email.
  const skipBlurValidationRef = useRef(false);
  const [email, setEmail] = useState(initialEmail);
  // Flag, not translated string — message is derived so locale switches update live.
  const [hasEmailError, setHasEmailError] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [suppressEmailDomainDropdown, setSuppressEmailDomainDropdown] =
    useState(false);

  const emailErrorMessage = hasEmailError ? invalidEmailMessage : '';

  const emailDomainSuggestions = useMemo(
    () => getEmailDomainSuggestions(email),
    [email],
  );

  const typedPrefixHighlightStyle = useMemo(
    () =>
      getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.content_primary,
      ) as TextStyle,
    [],
  );

  const domainSuggestionTextStyle = useMemo(
    () =>
      getTextStyle(
        pd.sys.typescale.body_text,
        pd.sys.colour.content_secondary,
      ) as TextStyle,
    [],
  );

  const emailDomainDropdownData = useMemo(
    (): ListViewProps[] =>
      emailDomainSuggestions.map((suggestion) => ({
        primaryText: suggestion,
        primaryTextStyle: domainSuggestionTextStyle,
        primaryHighlightText: buildEmailDomainHighlightText(
          email,
          suggestion,
          typedPrefixHighlightStyle,
          domainSuggestionTextStyle,
        ),
      })),
    [
      email,
      emailDomainSuggestions,
      domainSuggestionTextStyle,
      typedPrefixHighlightStyle,
    ],
  );

  const showEmailDomainDropdown =
    isEmailFocused &&
    !suppressEmailDomainDropdown &&
    emailDomainSuggestions.length > 0;

  const emailDomainDropdownHeight = getEmailDomainDropdownHeight(
    emailDomainSuggestions.length,
  );

  const validateAndSetEmailError = useCallback((value: string) => {
    setHasEmailError(!isValidEmail(value));
  }, []);

  const handleEmailChange = useCallback(
    (text: string) => {
      setSuppressEmailDomainDropdown(isCompleteEmailPaste(email, text));
      setEmail(text);
      // Don't show validation errors while typing; validate on blur/submit.
      setHasEmailError(false);
    },
    [email],
  );

  const handleEmailDomainSelect = useCallback(
    (item: ListViewProps) => {
      const selectedEmail = item.primaryText?.trim() ?? '';
      skipBlurValidationRef.current = true;
      setEmail(selectedEmail);
      setSuppressEmailDomainDropdown(false);
      setIsEmailFocused(false);
      validateAndSetEmailError(selectedEmail);
    },
    [validateAndSetEmailError],
  );

  const handleEmailClear = useCallback(() => {
    setEmail('');
    setHasEmailError(false);
    setSuppressEmailDomainDropdown(false);
  }, []);

  const dismissEmailDomainDropdown = useCallback(() => {
    setIsEmailFocused(false);
    emailFieldRef.current?.blur();
    Keyboard.dismiss();
  }, []);

  const handleEmailFocusChange = useCallback(
    (focused: boolean) => {
      if (focused === lastReportedFocusRef.current) {
        return;
      }
      lastReportedFocusRef.current = focused;
      setIsEmailFocused(focused);
      if (focused) {
        setSuppressEmailDomainDropdown(false);
        setHasEmailError(false);
        onEmailFocus?.();
        return;
      }
      if (skipBlurValidationRef.current) {
        skipBlurValidationRef.current = false;
        return;
      }
      if (email) {
        validateAndSetEmailError(email);
      }
    },
    [email, validateAndSetEmailError, onEmailFocus],
  );

  const emailClearEndIcon =
    email.length > 0
      ? {
          icon: pd.icon.main.navigation.cancel,
          height: 24,
          width: 24,
          tint: getColor(pd.sys.colour.content_secondary),
          testID: 'email-input-clear',
        }
      : undefined;

  return {
    email,
    emailErrorMessage,
    setHasEmailError,
    emailFieldRef,
    showEmailDomainDropdown,
    emailDomainDropdownData,
    emailDomainDropdownHeight,
    handleEmailChange,
    handleEmailDomainSelect,
    dismissEmailDomainDropdown,
    handleEmailFocusChange,
    emailClearEndIcon,
    onEmailEndIconPress: email.length > 0 ? handleEmailClear : undefined,
  };
};
